// Build the ordered screen list from the config + context.
// This is the entire phased-release mechanism: POST sections appear automatically
// once `enrolled` flips. Sex/answer visibility is handled at navigation time
// (isVisible), because gender/controlling answers are captured mid-flow.
import { isItemInPhase, isItemUnlocked } from './visibility.js';

const SCALE_PER_SCREEN = 7;

// `secure: false` leaves out the deferred password step (hidden in the PompaBooking embed).
export function buildScreens(config, { enrolled = false, additionalInfoEnabled = false, secure = true } = {}) {
  const assessmentIndex = Object.fromEntries(
    config.assessments.map((a, i) => [a.id, i]),
  );
  const screens = [];
  let prevA = null;

  // config.sections is pre-sorted by (assessment, order).
  for (const section of config.sections) {
    const items = section.items.filter(
      (it) =>
        isItemInPhase(it, enrolled) &&
        isItemUnlocked(it, section, { additionalInfoEnabled }),
    );
    if (!items.length) continue;
    const a = assessmentIndex[section.assessment];

    if (a !== prevA) {
      // The deferred "Secure your progress" step sits at the Assessment 1 -> 2 break.
      if (a === 1 && secure) screens.push({ type: 'secure' });
      screens.push({ type: 'intro', assessment: a });
      prevA = a;
    }

    if (section.scale) {
      for (let p = 0; p * SCALE_PER_SCREEN < items.length; p += 1) {
        screens.push({
          type: 'scale',
          assessment: a,
          sectionKey: section.key,
          page: p,
          items: items.slice(p * SCALE_PER_SCREEN, (p + 1) * SCALE_PER_SCREEN),
        });
      }
    } else {
      for (const item of items) {
        screens.push({ type: 'question', assessment: a, sectionKey: section.key, item });
      }
    }
  }

  screens.push({ type: 'done' });
  return screens;
}

// True if a screen has at least one answered id on it.
function anyAnswered(s, answers) {
  if (s.type === 'scale') return s.items.some((it) => answers[it.id] !== undefined);
  if (s.type === 'question') {
    const ids = s.item.fields ? s.item.fields.map((f) => f.id) : [s.item.id];
    return ids.some((id) => answers[id] !== undefined);
  }
  return false;
}

// True if every visible answerable id on a screen has a value.
function fullyAnswered(s, answers, profile, isVisibleFn) {
  if (s.type === 'scale') return s.items.every((it) => answers[it.id] !== undefined);
  if (s.type === 'question') {
    if (!isVisibleFn(s.item, answers, profile)) return true; // not shown → nothing to answer
    const ids = s.item.fields ? s.item.fields.map((f) => f.id) : [s.item.id];
    return ids.every((id) => answers[id] !== undefined);
  }
  return true;
}

// Index of the first screen whose content is not yet answered (for resume). A screen
// counts as answered when every visible answerable id on it has a value.
export function firstUnansweredIndex(screens, answers, profile, isVisibleFn) {
  for (let i = 0; i < screens.length; i += 1) {
    const s = screens[i];
    if (s.type === 'scale') {
      if (s.items.some((it) => answers[it.id] === undefined)) return i;
    } else if (s.type === 'question') {
      if (!isVisibleFn(s.item, answers, profile)) continue;
      // A "fields" item (e.g. name: firstName+lastName) has no answer under its own id —
      // its answers live under the sub-field ids. Check those instead.
      const ids = s.item.fields ? s.item.fields.map((f) => f.id) : [s.item.id];
      if (ids.some((id) => answers[id] === undefined)) return i;
    }
  }
  return 0;
}

// Where to drop a returning user. Questions are skippable, so "first unanswered" would
// yank them back to an early question they deliberately skipped. Instead resume to the
// FURTHEST screen they reached: find the last screen with any answer, then the first
// not-fully-answered screen at or after it (i.e. exactly where they stopped).
export function resumeIndex(screens, answers, profile, isVisibleFn) {
  let last = -1;
  for (let i = 0; i < screens.length; i += 1) {
    if (anyAnswered(screens[i], answers)) last = i;
  }
  if (last < 0) return 0;
  for (let i = last; i < screens.length; i += 1) {
    const s = screens[i];
    if (s.type === 'question' && !isVisibleFn(s.item, answers, profile)) continue;
    if (!fullyAnswered(s, answers, profile, isVisibleFn)) return i;
    // A non-question screen (intro/secure) after the furthest answer is their next step.
    if (s.type !== 'question' && s.type !== 'scale') return i;
  }
  return screens.length - 1;
}
